import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { fmt } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Chain-Break Audit
 *
 * Table: one row per period per account, sorted chronologically.
 * For each period we compare the stored opening balance against the
 * prior period's stored closing balance. A mismatch anywhere in the
 * chain is a "break" — the most common source of phantom variances
 * that appear to originate in a later period.
 */
export default function ChainCheck() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountFilter, setAccountFilter] = useState("");
  const [breaksOnly, setBreaksOnly] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/audit/chain-check`, {
        params: accountFilter ? { account_id: accountFilter } : {},
      })
      .then(r => { if (!cancel) setData(r.data); })
      .catch(e => { if (!cancel) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [accountFilter]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    return data.rows.filter(r => {
      if (!breaksOnly) return true;
      return r.bank_match === false || r.gl_match === false;
    });
  }, [data, breaksOnly]);

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }} data-testid="chain-check-page">
      <div style={{ marginBottom: 20 }}>
        <div className="heading h1">Chain-Break Audit</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4, maxWidth: 780 }}>
          Verifies that every period's stored opening balance matches the prior period's closing.
          A break here almost always predicts a phantom variance in a later period.
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <select
          value={accountFilter}
          onChange={e => setAccountFilter(e.target.value)}
          data-testid="chain-check-account-filter"
          style={{ padding: "6px 10px", fontSize: 13 }}
        >
          <option value="">All accounts</option>
          {data?.accounts?.map(a => (
            <option key={a.id} value={a.id}>
              {a.name || a.gl_account_code || a.id}
            </option>
          ))}
        </select>
        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={breaksOnly}
            onChange={e => setBreaksOnly(e.target.checked)}
            data-testid="chain-check-breaks-only"
          />
          Show breaks only
        </label>
        {data && (
          <span
            data-testid="chain-check-summary"
            style={{
              marginLeft: "auto",
              fontSize: 12.5,
              padding: "4px 10px",
              borderRadius: 999,
              background: data.break_count > 0 ? "#fdecea" : "#e6f4ea",
              color: data.break_count > 0 ? "#a12828" : "#1e6b32",
              fontWeight: 500,
            }}
          >
            {data.row_count} periods · {data.break_count} chain break{data.break_count === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {loading && <div className="muted">Loading…</div>}
      {error && (
        <div style={{ color: "#a12828", padding: 12, background: "#fdecea", borderRadius: 6 }}>
          Failed to load: {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="muted" style={{ padding: 20, textAlign: "center" }}>
          {breaksOnly ? "No chain breaks — every period rolls forward cleanly." : "No periods."}
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: "auto", border: "1px solid var(--line)", borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }} data-testid="chain-check-table">
            <thead>
              <tr style={{ background: "var(--surface-elev)", borderBottom: "1px solid var(--line-strong)" }}>
                <Th>Period</Th>
                <Th>Account</Th>
                <Th align="right">Bank opening</Th>
                <Th align="right">Expected (prior close)</Th>
                <Th align="right">Bank Δ</Th>
                <Th>Bank match?</Th>
                <Th align="right">GL opening</Th>
                <Th align="right">Expected (prior close)</Th>
                <Th align="right">GL Δ</Th>
                <Th>GL match?</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r.account_id}-${r.period_id}`}
                  style={{
                    borderBottom: "1px solid var(--line)",
                    background:
                      r.bank_match === false || r.gl_match === false
                        ? "#fff8e1"
                        : (i % 2 ? "var(--surface-elev)" : "transparent"),
                  }}
                >
                  <Td>{r.period_label || r.month_year || r.period_id?.slice(0, 8)}</Td>
                  <Td>
                    {r.account_name}
                    <div className="muted" style={{ fontSize: 10.5 }}>{r.account_number}</div>
                  </Td>
                  <Td align="right" mono>{r.bank_opening != null ? `$${fmt.currency(r.bank_opening)}` : "—"}</Td>
                  <Td align="right" mono>{r.expected_bank_opening != null ? `$${fmt.currency(r.expected_bank_opening)}` : "—"}</Td>
                  <Td align="right" mono>
                    {r.bank_delta != null && Math.abs(r.bank_delta) >= 0.005
                      ? <span style={{ color: "#a12828", fontWeight: 500 }}>${fmt.currency(r.bank_delta)}</span>
                      : "—"}
                  </Td>
                  <MatchCell v={r.bank_match} />
                  <Td align="right" mono>{r.gl_opening != null ? `$${fmt.currency(r.gl_opening)}` : "—"}</Td>
                  <Td align="right" mono>{r.expected_gl_opening != null ? `$${fmt.currency(r.expected_gl_opening)}` : "—"}</Td>
                  <Td align="right" mono>
                    {r.gl_delta != null && Math.abs(r.gl_delta) >= 0.005
                      ? <span style={{ color: "#a12828", fontWeight: 500 }}>${fmt.currency(r.gl_delta)}</span>
                      : "—"}
                  </Td>
                  <MatchCell v={r.gl_match} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th style={{
      padding: "8px 10px",
      textAlign: align,
      fontWeight: 600,
      fontSize: 11.5,
      color: "var(--ink-mute)",
      textTransform: "uppercase",
      letterSpacing: ".03em",
      whiteSpace: "nowrap",
    }}>{children}</th>
  );
}

function Td({ children, align = "left", mono = false }) {
  return (
    <td style={{
      padding: "8px 10px",
      textAlign: align,
      fontFamily: mono ? "var(--mono)" : undefined,
      whiteSpace: "nowrap",
    }}>{children}</td>
  );
}

function MatchCell({ v }) {
  if (v == null) {
    return <Td><span className="muted">n/a</span></Td>;
  }
  return (
    <Td>
      <span style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        background: v ? "#e6f4ea" : "#fdecea",
        color: v ? "#1e6b32" : "#a12828",
      }}>{v ? "✓ match" : "✗ break"}</span>
    </Td>
  );
}
